import type { OrderLineTypes } from "@repo/database";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";
import { useShallow } from "zustand/react/shallow";

import CartItemContextDialog from "~/components/cart/cart-item-context-dialog";
import CartItemExtraDialog from "~/components/cart/cart-item-extra-dialog";
import MiscProductDialog from "~/components/cart/misc-product-dialog";
import NoTableSelectedDialog from "~/components/cart/no-table-selected-dialog";
import OrderCommentDialog from "~/components/cart/order-comment-dialog";
import OrderDiscountDialog from "~/components/cart/order-discount-dialog";
import { CartContext } from "~/hooks/use-cart";
import { useOrder } from "~/hooks/use-order";
import { useCartStore } from "~/stores/use-cart-store";
import { useOrderStore } from "~/stores/use-order-store";
import { UnsavedChangesProvider } from "./unsaved-changes-provider";

export interface CartItem {
  reference: string;
  parentReference?: string;
  name: string;
  type: OrderLineTypes;
  vatRate: number;
  vatRateName?: string;
  value: number;
  ordered: boolean;
  orderId?: string;
  printed: boolean;
  createdAt: Date;
  quantity: number;
  productId?: string;
  categoryId?: string;
  turnoverGroupName?: string;
  turnoverGroupLedgerNumber?: number;
  categoryName?: string;
  courseId?: string;
  courseName?: string;
  employeeId?: string;
  printRouteId?: string;
  notifyPrintRouteId?: string;
  preparationName?: string;
  originalQuantity: number;
  orderId: string;
}

interface CartProviderProps {
  children: ReactNode;
}

export function CartProvider({ children }: CartProviderProps) {
  const { reset, setItems, clear } = useCartStore(
    useShallow((state) => ({
      addItem: state.addItem,
      setItems: state.setItems,
      reset: state.reset,
      clear: state.clear,
    })),
  );

  const router = useRouter();

  const { orders } = useOrder();

  const { currentOrder, setCurrentOrder } = useOrderStore();

  useEffect(() => {
    const orderItems =
      currentOrder?.items.map((item) => ({
        ...item,
        ordered: true,
        printed: true,
        originalQuantity: item.quantity,
        reference: item.reference,
        vatRate: item.vatRate,
        value: item.value,
        courseId: item.courseId,
        productId: item.productId,
        parentReference: item.parentReference ?? undefined,
        orderId: currentOrder.id,
      })) ?? [];

    console.log(orderItems);

    setItems(orderItems);
  }, [currentOrder?.items, orders, setItems, clear, currentOrder?.id]);

  useEffect(() => {
    const unsub = router.subscribe("onResolved", (e) => {
      if (!e.toLocation.pathname.includes("/cash-register")) {
        setCurrentOrder(undefined);
      }
    });

    return () => {
      unsub();
    };
  }, [router, setCurrentOrder]);

  const context = {
    reset,
  };

  return (
    <CartContext.Provider value={context}>
      <UnsavedChangesProvider>{children}</UnsavedChangesProvider>
      <OrderCommentDialog />
      <CartItemExtraDialog />
      <OrderDiscountDialog />
      <CartItemContextDialog />
      <MiscProductDialog />
      <NoTableSelectedDialog />
    </CartContext.Provider>
  );
}
